import {
  Button,
  Card,
  ChannelAvatar,
  EmptyState,
  Flex,
  Heading,
  Link,
  RefreshCwIcon,
  Text,
} from '@buffer-mono/popcorn'

import type { ChannelMask } from '~publish/pages/Settings/hooks/useChannelsForSettings'
import styles from './ChannelNeedsRefresh.module.css'
import { useHasPrivilege } from '~publish/pages/Settings/hooks/useHasPrivilege'
import { useRefreshChannelConnection } from '~publish/hooks/useRefreshChannelConnection'
import {
  Status,
  getChannelStatus,
} from '~publish/pages/Settings/util/getChannelStatus'
import clsx from 'clsx'
import { getChannelName } from '~publish/helpers/channels'
import type { ChannelType } from '~publish/pages/Comments/types'

const HELP_CENTER_URL =
  'https://support.buffer.com/?openContact=true&utm_source=buffer&utm_medium=channel-error-link&utm_campaign=comments-threads-refresh'

export function ChannelNeedsRefresh({
  channel,
}: {
  channel: ChannelType
}): JSX.Element {
  const canManageChannels = useHasPrivilege('canManageChannels')

  return (
    <EmptyState size="xlarge" variant="primary" className={styles.emptyState}>
      <EmptyState.Icon>
        <RefreshCwIcon />
      </EmptyState.Icon>
      <EmptyState.Heading className={styles.header}>
        <Flex direction="column" gap="2xs" align="center">
          <Heading as="h2">Refresh to get started with Community</Heading>
          <Text color="subtle">
            After refreshing your connection, you’ll be able to reply to
            comments.
          </Text>
        </Flex>
      </EmptyState.Heading>
      <EmptyState.Actions className={styles.actions}>
        <Flex direction="column" gap="md" fullWidth>
          <ChannelCard channel={channel as unknown as ChannelMask} />
          {canManageChannels ? (
            <Text color="subtle" className={styles.footer}>
              Having trouble refreshing?{' '}
              <Link
                href={HELP_CENTER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link}
              >
                Get in touch
              </Link>
            </Text>
          ) : (
            <Text color="critical" className={styles.footer}>
              You don&apos;t have permission to refresh the connection. Please
              contact the administrator of your organization.
            </Text>
          )}
        </Flex>
      </EmptyState.Actions>
    </EmptyState>
  )
}

export interface ChannelCardProps {
  channel: ChannelMask
}

/**
 * ChannelCard is a component that displays a channel card with a refresh button.
 * @param channel - The channel to display.
 * @returns A channel card with a refresh button.
 */
const ChannelCard = ({ channel }: ChannelCardProps): JSX.Element => {
  const refreshChannelConnection = useRefreshChannelConnection()
  const canManageChannels = useHasPrivilege('canManageChannels')

  const handleRefreshChannel = (cta: string): void => {
    canManageChannels &&
      refreshChannelConnection({
        channel,
        cta,
      })
  }

  const { status } = getChannelStatus({
    channel,
  })
  const channelDescriptor = getChannelName(channel.service)

  return (
    <Card
      padding="small"
      className={clsx(styles.base, status === Status.Locked && styles.locked)}
    >
      <Flex direction="row" gap="md" align="center">
        <ChannelAvatar
          src={channel.avatar}
          alt={channel.displayName || channel.name || 'Channel avatar'}
          channel={channel.service}
          size="mid"
          className={styles.avatar}
        />
        <div className={styles.channelRowCenter}>
          <Text
            weight="bold"
            className={clsx(status === Status.Locked && styles.locked)}
          >
            {channel.name || channel.displayName}
          </Text>
          <Text
            color={status === Status.Locked ? 'disabled' : 'subtle'}
            className={styles.channelDescriptor}
          >
            {channelDescriptor}
          </Text>
        </div>
      </Flex>
      <div className={styles.channelRowRight}>
        <Button
          variant="primary"
          size="large"
          disabled={!canManageChannels}
          onClick={(): void =>
            handleRefreshChannel('publish-comments-button-refresh-1')
          }
        >
          Refresh
        </Button>
      </div>
    </Card>
  )
}
